/**
 * Parkify Sales Portal theme.
 *
 * Brand hexes are sourced from the canonical @parkify/brand-tokens package —
 * the single source of truth for Master Purple, Master Black, the warm grays,
 * and the Stack accents (Green stays banned: there is no green key). The
 * portal's own alias key names (purple/gray/bg/divider/…) are preserved so no
 * component has to change. Only the sales-portal-specific semantic surfaces
 * (text opacities, warning tints, purple hover tints) remain as literals.
 */

import { colors as brand } from '@parkify/brand-tokens'

export const colors = {
  // ── Master (primary) palette — from @parkify/brand-tokens ─────────────────
  purple: brand.brand,        // Master Purple — hero bg, headers, CTAs
  black: brand.black,         // Master Black — body text, footer bg
  white: brand.white,         // Master White
  gray: brand.gray500,        // Master Gray — borders, disabled, secondary text
  brown: brand.stackBrown,    // Legacy Stack Brown — existing accents only

  // ── Stack (secondary, accent-only) palette ──────────────────────────────
  orange: brand.stackOrange,  // Stack Orange — alerts, highlights
  red: brand.stackRed,        // Stack Red — error, urgency
  blue: brand.stackBlue,      // Stack Blue — links, info callouts, data

  // ── Background defaults ─────────────────────────────────────────────────
  bg: brand.gray200,          // Page background (Light Purple-Gray)
  bgAlt: brand.gray100,       // Alternate panel background
  panel: brand.white,         // Primary content panel
  divider: brand.gray300,     // 1px panel separator

  // ── Body text aliases ───────────────────────────────────────────────────
  text: brand.black,
  textMuted: 'rgba(35,31,32,0.65)',   // Master Black @ 65% (sales-portal local)
  textDim: 'rgba(35,31,32,0.5)',      // Master Black @ 50%

  // ── Semantic surfaces (sales-portal-specific, not core brand tokens) ──────
  warningBg: '#fff5f5',
  warningBorder: 'rgba(237,28,36,0.2)',
  infoBg: brand.brand,    // Rule/info boxes get the Master Purple treatment
  purpleSoft: 'rgba(127,63,152,0.08)',  // Hover/selected tints
  purpleSoftHi: 'rgba(127,63,152,0.16)',
} as const

export { fontFamily } from '@parkify/brand-tokens'

// Brand type-scale references (clamp() formulas come from the guidelines).
export const type = {
  hero: { size: 'clamp(40px, 6vw, 80px)', weight: 700, tracking: '-2px' },
  section: { size: 'clamp(32px, 5vw, 60px)', weight: 700, tracking: '-1.5px' },
  body: { size: '14px', weight: 400 },
  // Eyebrow / section labels — 10–11px uppercase, 2.5–3px tracking, bold.
  eyebrow: { size: '10px', weight: 700, tracking: '3px' },
  sectionLabel: { size: '10px', weight: 700, tracking: '2.5px' },
  navLink: { size: '11px', weight: 700, tracking: '1.2px' },
} as const

export const radii = { sm: 4, md: 8, lg: 12, xl: 18 } as const

export const shadow = {
  card: '0 2px 8px rgba(35,31,32,0.06)',
  cardHi: '0 10px 32px rgba(35,31,32,0.10)',
  modal: '0 20px 60px rgba(35,31,32,0.18)',
} as const
